import * as React from 'react';

import { isValidDate } from '@gitbook/util-format';

import { Tooltip } from '../Tooltip';
import { useDateShortRelative } from './useDateShortRelative';

/**
 * Display a date as a short relative indicator (ex: "16hr", "2min")
 *
 * Useful links regarding the correct abbreviations:
 * https://www.aresearchguide.com/monthdayabb.html
 * https://www.quora.com/What-is-the-proper-way-to-abbreviate-the-word-day
 */
export function DateShortRelative({
    ref,
    ...props
}: {
    value: Date | string | number;
    prefix?: string;
    /**
     * Display only the date.
     * @default true
     */
    onlyDate?: boolean;
    className?: string;
    children?: React.ReactNode;
} & { ref?: React.ForwardedRef<HTMLTimeElement> }): React.ReactElement {
    const { value, onlyDate = true, prefix, ...rest } = props;
    const {
        date,
        value: humanPeriod,
        accessibilityValue,
        tooltip,
    } = useDateShortRelative(value, { onlyDate, prefix });

    return (
        <Tooltip label={tooltip}>
            <time
                ref={ref}
                dateTime={isValidDate(date) ? date.toISOString() : undefined}
                aria-label={accessibilityValue || undefined}
                {...rest}
            >
                {humanPeriod}
            </time>
        </Tooltip>
    );
}
